'use client';

import { useEffect, useState } from 'react';
import { MoreVertical, Edit, Trash2, MessageCircle, Heart } from 'lucide-react';
import { News } from '@/types';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useAuthModal } from '@/context/AuthModalContext';
import { useNewsStore } from '@/store/newsStore';
import { useGroupStore } from '@/store/groupStore';
import { ImageCarousel } from './ImageCarousel';
import { NewsCommentSection } from './NewsCommentSection';
import { postAnchorId } from './postAnchor';
import CreateNewsModal from '../ui/CreateNewsModal';
import { ReportButton } from '../ui/ReportButton';
import Link from 'next/link';
import { useLanguage } from '@/context/LanguageContext';
import { ActivityLogger } from '@/lib/activityLog';
import { linkifyText, truncateAtSafeBoundary } from '@/lib/linkify';
import { Toast } from '@/components/ui';
import { getUserProfileHref } from '@/utils';

interface NewsCardProps {
  news: News;
  onDelete?: () => void;
}

const MAX_CONTENT_LENGTH = 300;

export function NewsCard({ news, onDelete }: NewsCardProps) {
  const { t, language } = useLanguage();
  const { currentUser, isFollowingGroup, toggleFollowGroup } = useAuth();
  const { openAuthModal } = useAuthModal();
  const { deleteNews, fetchComments, commentsByNews, commentsFailedFor, toggleLike, hasUserLiked } = useNewsStore();
  const { getUserRole } = useGroupStore();

  const [showMenu, setShowMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' } | null>(null);

  const isAuthor = currentUser?.id === news.author?.id;
  const showFollowButton = !!news.group;
  const isFollowingTarget = news.group ? isFollowingGroup(news.group.documentId) : false;
  const followLabel = isFollowingTarget ? t.common.unfollow : t.common.follow;

  // Check if user is MWBI admin
  const isAdmin = currentUser?.role?.type === 'admin' || currentUser?.role?.type === 'superadmin';

  // Check if user can edit/delete group posts
  const canManageGroupPost = news.group && currentUser
    ? (() => {
        const role = getUserRole(currentUser.id, news.group.id);
        return role === 'admin' || role === 'editor';
      })()
    : false;

  const canEdit = isAuthor || canManageGroupPost || isAdmin;
  // MAX_CONTENT_LENGTH is a target, not a hard stop: the cut flexes to the nearest natural
  // boundary so a preview never ends mid-word or mid-link. The link half matters beyond
  // tidiness -- linkifyText below cannot tell a whole URL from a beheaded one, so half an
  // /events/<documentId> address still renders as a real, clickable link to an event that
  // does not exist.
  const contentPreview = truncateAtSafeBoundary(news.content, MAX_CONTENT_LENGTH);
  const contentTruncated = contentPreview.truncated;
  const displayContent = showFullContent ? news.content : contentPreview.text;

  const comments = commentsByNews[news.documentId] || [];
  const isLiked = currentUser ? hasUserLiked(news.documentId, currentUser.id) : false;

  const anchorId = postAnchorId(news.documentId);
  // A failed comment load must not read as "this post has no comments" (MWBI-934).
  const commentsUnavailable = Boolean(commentsFailedFor[news.documentId]);
  const commentsErrorId = `${anchorId}-comments-error`;
  const contentBlockId = `${anchorId}-content`;

  // Auto-expand when this post is the one named in the URL hash (MWBI-935).
  // Post-comment emails deep-link to /groups/<id>?tab=posts#post-<documentId>;
  // arriving from "a new comment was posted" should show the comment, not a
  // collapsed card. Mirrors GroupTopicCard's per-card effect, with the parent
  // (GroupPostsTab) owning scroll/highlight/focus + hash cleanup.
  //
  // The fetchComments call is deliberate even though GroupPostsTab has already
  // batch-fetched this post: the reader arrived from a "new comment" email, so
  // the one post they came for is refetched to guarantee the comment that
  // triggered the email is present rather than a batch snapshot taken earlier.
  //
  // Every dep is stable for the lifetime of a given card -- `anchorId` and
  // `documentId` identify the post, and zustand actions keep their identity
  // across renders -- so this runs once per mount and a manual collapse is
  // never undone by a re-render. Do NOT add `comments` (or any other value
  // that changes as data arrives) to the deps: that would re-open the section
  // under the reader every time a comment loaded.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Compare against the ENCODED anchor so the URL hash (which the email
    // percent-encodes) matches the rendered id for any documentId.
    if (window.location.hash !== `#${anchorId}`) return;

    setShowComments(true);
    void fetchComments(news.documentId);
  }, [anchorId, fetchComments, news.documentId]);

  // Collect unique comment authors
  const uniqueCommentAuthors = (() => {
    const seen = new Set<number>();
    return comments.filter(c => {
      if (!c.author?.id || seen.has(c.author.id)) return false;
      seen.add(c.author.id);
      return true;
    }).map(c => c.author);
  })();

  const authorProfileHref = news.group
    ? `/groups/${news.group.documentId}`
    : getUserProfileHref(news.author ? { id: news.author.id } : { id: 0 });

  const handleDelete = async () => {
    const success = await deleteNews(news.documentId);
    if (success) {
      if (currentUser) {
        ActivityLogger.deletePost(currentUser, news.content.substring(0, 50) + (news.content.length > 50 ? '...' : ''), news.documentId);
      }
      onDelete?.();
    }
    setShowDeleteConfirm(false);
  };

  const handleShowComments = async () => {
    if (!showComments) {
      await fetchComments(news.documentId);
    }
    setShowComments(!showComments);
  };

  const handleLike = async () => {
    if (!currentUser) {
      openAuthModal();
      return;
    }

    const wasLiked = hasUserLiked(news.documentId, currentUser.id);
    await toggleLike(news.documentId);

    if (!wasLiked) {
      ActivityLogger.likePost(
        currentUser,
        news.content.substring(0, 50) + (news.content.length > 50 ? '...' : ''),
        news.documentId
      );
    }
  };

  const handleFollow = async () => {
    if (followLoading) return;

    if (!currentUser) {
      openAuthModal();
      return;
    }

    setFollowLoading(true);

    try {
      if (news.group) {
        await toggleFollowGroup(news.group.documentId);
        setToast({
          message: isFollowingTarget
            ? t.groupDetail.unfollowed.replace('{name}', news.group.name)
            : t.groupDetail.nowFollowing.replace('{name}', news.group.name),
          type: 'success'
        });
        setTimeout(() => setToast(null), 3000);
      }
    } finally {
      setFollowLoading(false);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return t.time.justNow;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}${t.time.minutesAgo}`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}${t.time.hoursAgo}`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}${t.time.daysAgo}`;

    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <article
        id={anchorId}
        className="bg-white border border-border rounded-lg p-4 overflow-hidden hover:shadow-md transition focus-within:ring-2 focus-within:ring-primary scroll-mt-4"
        tabIndex={0}
        role="article"
        aria-label={`Post by ${news.author?.displayName || 'Unknown'}`}
        data-testid={`news-card-${news.documentId}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            {/* Avatar - Clickable to group or user profile */}
            <Link
              href={authorProfileHref}
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden hover:opacity-80 transition"
              style={{ backgroundColor: news.author?.avatarColor || '#00423E' }}
            >
              {news.group?.avatar?.url ? (
                <img
                  src={news.group.avatar.url}
                  alt={news.group.name}
                  className="w-full h-full object-cover"
                />
              ) : news.author?.avatarUrl ? (
                <img
                  src={news.author.avatarUrl}
                  alt={news.author?.displayName || 'Unknown'}
                  className="w-full h-full object-contain"
                />
              ) : (
                <span>{news.group ? news.group.name.substring(0, 2).toUpperCase() : (news.author?.initials || '??')}</span>
              )}
            </Link>

            {/* Author Info - Show group name if posting as group */}
            <div>
              <Link
                href={authorProfileHref}
                className="font-semibold text-foreground hover:underline"
                data-testid="news-card-author-link"
              >
                {news.group ? news.group.name : (news.author?.displayName || 'Unknown')}
              </Link>
              <div className="flex items-center gap-2 text-sm text-foreground-muted">
                <time dateTime={news.createdAt}>{formatTimeAgo(news.createdAt)}</time>
              </div>
            </div>
          </div>

          {/* Report & Menu */}
          <div className="flex items-center gap-2">
            {showFollowButton && (
              <button
                onClick={handleFollow}
                disabled={followLoading}
                className="inline-flex items-center px-3 py-2 rounded-md border border-border bg-white text-black text-sm font-medium transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-60"
                aria-label={`${followLabel} ${news.group!.name}`}
                data-testid="news-card-follow-btn"
              >
                <span>{followLabel}</span>
              </button>
            )}
            {currentUser && !isAuthor && (
              <ReportButton contentType="post" contentId={news.documentId} contentTitle={news.content.substring(0, 50)} />
            )}
            {canEdit && (
              <div className="relative">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-1 hover:bg-sidebar-hover rounded transition focus:outline-none focus:ring-2 focus:ring-primary"
                  aria-label="Post options"
                  aria-expanded={showMenu}
                  data-testid="news-card-options-btn"
                >
                  <MoreVertical size={20} className="text-foreground-muted" />
                </button>
                {showMenu && (
                  <div className="absolute right-0 mt-1 w-40 bg-background border border-border rounded-md shadow-lg z-10">
                    <button
                      onClick={() => { setShowEditModal(true); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-sidebar-hover transition"
                      data-testid="news-card-edit-menu-item"
                    >
                      <Edit size={16} />
                      {t.common.edit}
                    </button>
                    <button
                      onClick={() => { setShowDeleteConfirm(true); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition"
                      data-testid="news-card-delete-menu-item"
                    >
                      <Trash2 size={16} />
                      {t.common.delete}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Images */}
        {news.images && news.images.length > 0 && (
          <div className="mb-3 max-sm:-mx-4">
            <ImageCarousel images={news.images} containerClassName="max-sm:rounded-none" />
          </div>
        )}

        {/* Content */}
        <div className="mb-3 pb-3 border-b border-border">
          {/* The ellipsis is our own affordance, not authored text, so it is appended as a
              sibling node rather than concatenated into linkifyText's input -- the URL regex
              would otherwise swallow it and the trailing-punctuation stripper hand it back. */}
          <div id={contentBlockId} className="text-foreground whitespace-pre-wrap break-words" data-testid="news-card-content-block">
            {linkifyText(displayContent)}
            {contentTruncated && !showFullContent ? '...' : null}
          </div>
          {contentTruncated && (
            <button
              onClick={() => setShowFullContent(!showFullContent)}
              aria-expanded={showFullContent}
              aria-controls={contentBlockId}
              className="text-foreground font-medium text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-primary rounded"
              data-testid="news-card-expand-content-btn"
            >
              {showFullContent ? `...${t.common.seeLess.toLowerCase()}` : `...${t.common.seeMore.toLowerCase()}`}
            </button>
          )}
        </div>

        {/* Actions with Comment Avatars */}
        <div className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={handleShowComments}
                className="flex items-center gap-1 text-foreground-muted hover:text-primary transition focus:outline-none focus:ring-2 focus:ring-primary rounded px-2 py-1"
                aria-label={commentsUnavailable ? t.news.commentsUnavailable : `${comments.length} comments`}
                aria-describedby={commentsUnavailable ? commentsErrorId : undefined}
                data-testid="news-card-comment-toggle-btn"
              >
                <MessageCircle size={18} />
                {/* An em dash, never "0" -- the count is unknown, not zero. */}
                <span className="text-sm">{commentsUnavailable ? '—' : comments.length}</span>
              </button>
              {comments.length > 0 && (
                <div className="flex items-center -space-x-2">
                  {uniqueCommentAuthors.slice(0, 3).map((author, index) => {
                    if (!author) return null;
                    return (
                      <div
                        key={author.id}
                        className="w-6 h-6 rounded-full border-2 border-background overflow-hidden flex-shrink-0"
                        style={{ zIndex: 3 - index }}
                        title={author.displayName}
                      >
                        {author.avatarUrl ? (
                          <img
                            src={author.avatarUrl}
                            alt={author.displayName}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center text-white font-bold text-xs"
                            style={{ backgroundColor: author.avatarColor }}
                          >
                            {author.initials}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {uniqueCommentAuthors.length > 3 && (
                    <div
                      className="w-6 h-6 rounded-full border-2 border-background bg-background-secondary flex items-center justify-center flex-shrink-0"
                      style={{ zIndex: 0 }}
                    >
                      <span className="text-foreground-muted font-semibold text-xs">
                        +{uniqueCommentAuthors.length - 3}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button
              onClick={handleLike}
              className={`flex items-center gap-1 transition-colors focus:outline-none focus:ring-2 focus:ring-primary rounded px-2 py-1 ${
                isLiked ? 'text-red-500' : 'text-foreground-muted hover:text-red-500'
              }`}
              aria-label={`${news.likes} ${t.common.likes}`}
              aria-pressed={isLiked}
              type="button"
              data-testid="news-card-like-btn"
            >
              <Heart size={18} fill={isLiked ? 'currentColor' : 'none'} />
              <span className="text-sm">{news.likes}</span>
            </button>
          </div>
        </div>

        {/* Comment load failure -- announced, and referenced by the toggle's
            aria-describedby so non-visual users get the reason, not a bare count. */}
        {commentsUnavailable && (
          <p
            id={commentsErrorId}
            role="alert"
            className="text-sm text-red-600 pb-3"
            data-testid="news-card-comments-error"
          >
            {t.news.commentsUnavailable}
          </p>
        )}

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4">
            <NewsCommentSection
              newsDocumentId={news.documentId}
              newsTitle={news.content.substring(0, 50) + (news.content.length > 50 ? '...' : '')}
              comments={comments}
            />
          </div>
        )}
      </article>

      {/* Edit Modal */}
      {showEditModal && (
        <CreateNewsModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          editMode={true}
          newsDocumentId={news.documentId}
          initialContent={news.content}
          initialImages={news.images || []}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="news-card-delete-confirm-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative bg-background rounded-lg p-6 max-w-sm mx-4 space-y-4">
            <h3 className="text-lg font-semibold text-foreground">{t.news.deletePost}</h3>
            <p className="text-foreground-muted">{t.news.deleteConfirm}</p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-foreground-muted hover:bg-background-secondary rounded-lg transition"
                data-testid="news-card-delete-cancel-btn"
              >
                {t.common.cancel}
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                data-testid="news-card-delete-confirm-btn"
              >
                {t.common.delete}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
